import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { Person as PersonIcon, Save as SaveIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  userService,
  type UpdateProfileData,
  type User,
} from '../services/user.service';
import { useAuth } from '../contexts/AuthContext';

export const UserProfile = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const initializedRef = useRef(false);
  const successTimeoutRef = useRef<ReturnType<
    typeof globalThis.setTimeout
  > | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch user profile
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery<User>({
    queryKey: ['userProfile'],
    queryFn: userService.getMyProfile,
  });

  // Initialize form state from profile (only once when data first loads)
  useEffect(() => {
    if (profile && !initializedRef.current) {
      initializedRef.current = true;
      // Use a microtask to avoid the React warning about setState in effect
      globalThis.queueMicrotask(() => {
        setName(profile.name);
        setAvatar(profile.avatar || '');
      });
    }
  }, [profile]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        globalThis.clearTimeout(successTimeoutRef.current);
      }
    };
  }, []);

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateProfileData) => userService.updateMyProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      refreshUser();
      setEditMode(false);
      setSaveSuccess(true);
      setSaveError(null);
      successTimeoutRef.current = globalThis.setTimeout(
        () => setSaveSuccess(false),
        3000
      );
    },
    onError: (err: Error) => {
      setSaveError(err.message);
    },
  });

  const handleSave = () => {
    updateMutation.mutate({ name, avatar: avatar || undefined });
  };

  const handleCancel = () => {
    if (profile) {
      setName(profile.name);
      setAvatar(profile.avatar || '');
    }
    setEditMode(false);
    setSaveError(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{t('profile.loadError')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('profile.title')}
      </Typography>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {t('profile.saveSuccess')}
        </Alert>
      )}

      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar
                src={profile?.avatar}
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
              >
                <PersonIcon sx={{ fontSize: 60 }} />
              </Avatar>
              <Typography variant="h6">{profile?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {profile?.email}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  mt: 1,
                  px: 1.5,
                  py: 0.5,
                  bgcolor:
                    profile?.role === 'admin' ? 'primary.main' : 'grey.500',
                  color: 'white',
                  borderRadius: 1,
                }}
              >
                {profile?.role === 'admin'
                  ? t('user.roleAdmin')
                  : t('user.roleUser')}
              </Typography>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                {t('profile.accountInfo')}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('profile.memberSince')}
                </Typography>
                <Typography variant="body2">
                  {profile?.createdAt
                    ? new Date(profile.createdAt).toLocaleDateString()
                    : '-'}
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('profile.lastLogin')}
                </Typography>
                <Typography variant="body2">
                  {profile?.lastLogin
                    ? new Date(profile.lastLogin).toLocaleString()
                    : '-'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Edit Profile */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography variant="h6">{t('profile.editProfile')}</Typography>
              {!editMode && (
                <Button variant="outlined" onClick={() => setEditMode(true)}>
                  {t('common.edit')}
                </Button>
              )}
            </Box>

            <TextField
              fullWidth
              label={t('profile.name')}
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={!editMode}
              margin="normal"
            />

            <TextField
              fullWidth
              label={t('profile.email')}
              value={profile?.email || ''}
              disabled
              margin="normal"
              helperText={t('profile.emailCannotChange')}
            />

            <TextField
              fullWidth
              label={t('profile.avatarUrl')}
              value={avatar}
              onChange={e => setAvatar(e.target.value)}
              disabled={!editMode}
              margin="normal"
              placeholder="https://example.com/avatar.jpg"
              helperText={t('profile.avatarHint')}
            />

            {editMode && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    t('common.save')
                  )}
                </Button>
                <Button variant="outlined" onClick={handleCancel}>
                  {t('common.cancel')}
                </Button>
              </Box>
            )}

            {/* Teams Section */}
            {profile?.userTeams && profile.userTeams.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom>
                  {t('profile.teams')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {profile.userTeams.map(ut => (
                    <Box
                      key={ut.team.id}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                      }}
                    >
                      {ut.team.name}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};
